import { formatIsoDate } from '@umodeapporg/ui/helpers/dateHelpers';
import type { TFunction } from 'i18next';

/** How the second selector of the builder collects the value of one filter. */
export type TFilterBuilderKind = 'select' | 'date' | 'date-range' | 'text';

export interface IFilterBuilderOption {
  value: string;
  label: string;
}

interface IFilterBuilderFieldBase {
  /** Identifies the filter in the value map, and is what the screen maps to its own state. */
  key: string;
  /** Name of the filter: the first selector lists it and the chip is titled by it. */
  label: string;
}

export interface IFilterBuilderSelectField extends IFilterBuilderFieldBase {
  kind: 'select';
  options: Array<IFilterBuilderOption>;
  searchable?: boolean;
  /** Off for a filter that only honours one value, e.g. one the API queries by. */
  multiple?: boolean;
}

export interface IFilterBuilderDateField extends IFilterBuilderFieldBase {
  kind: 'date';
}

export interface IFilterBuilderDateRangeField extends IFilterBuilderFieldBase {
  kind: 'date-range';
  fromLabel: string;
  toLabel: string;
}

export interface IFilterBuilderTextField extends IFilterBuilderFieldBase {
  kind: 'text';
  placeholder: string;
}

export type TFilterBuilderField =
  IFilterBuilderSelectField | IFilterBuilderDateField | IFilterBuilderDateRangeField | IFilterBuilderTextField;

/**
 * Every value is a list of strings, so one bar serves every kind: a select holds the chosen
 * options, a text holds the typed term, a date holds the day and a range holds `[from, to]`,
 * where an empty end means "open on that side".
 */
export type TFilterBuilderValues = Record<string, Array<string>>;

export const EMPTY_FILTER_BUILDER_VALUES: TFilterBuilderValues = {};

/**
 * One confirmed chip. `replaces` carries the type the person switched AWAY from while
 * editing, so the screen drops it and adds the new one in a SINGLE write: two writes in the
 * same render would both start from the same state and the removal would be lost.
 */
export interface IFilterChange {
  key: string;
  value: Array<string>;
  replaces: string | null;
}

const RANGE_FROM = 0;
const RANGE_TO = 1;

/** How many values a chip spells out before it summarises the rest as "+N". */
const CHIP_VALUES_SHOWN = 2;

export interface IFilterDateRange {
  from: string;
  to: string;
}

export function readDateRange(value: Array<string> | undefined): IFilterDateRange {
  return { from: value?.[RANGE_FROM] ?? '', to: value?.[RANGE_TO] ?? '' };
}

export function buildDateRange(range: IFilterDateRange): Array<string> {
  return [range.from, range.to];
}

/** The lone value of a single-valued filter: one date, one term, one chosen option. */
export function readSingleValue(value: Array<string> | undefined): string {
  return value?.[0] ?? '';
}

export function buildSingleValue(value: string): Array<string> {
  return value === '' ? [] : [value];
}

/** A filter is applied once it carries at least one value worth narrowing the list by. */
export function isFilterApplied(value: Array<string> | undefined): boolean {
  return (value ?? []).some((entry) => entry.trim() !== '');
}

export function hasAppliedFilters(values: TFilterBuilderValues): boolean {
  return Object.values(values).some(isFilterApplied);
}

/** What a select value reads as: the option label when it is known, the raw value otherwise. */
function optionLabel(field: IFilterBuilderSelectField, value: string): string {
  return field.options.find((option) => option.value === value)?.label ?? value;
}

function dateRangeText(value: Array<string> | undefined, t: TFunction): string {
  const { from, to } = readDateRange(value);
  if (from !== '' && to !== '') {
    return t('filters.periodo', { de: formatIsoDate(from), ate: formatIsoDate(to) });
  }
  if (from !== '') {
    return t('filters.periodoDe', { de: formatIsoDate(from) });
  }
  return t('filters.periodoAte', { ate: formatIsoDate(to) });
}

/**
 * What the chip says after the filter name. Several values read as a list, because the
 * filter lets any of them through, and a long list stops at the first two plus a count, so
 * one chip never takes the whole bar.
 */
export function filterValueText(field: TFilterBuilderField, value: Array<string> | undefined, t: TFunction): string {
  if (field.kind === 'date-range') {
    return dateRangeText(value, t);
  }
  if (field.kind === 'date') {
    return formatIsoDate(readSingleValue(value));
  }
  if (field.kind === 'text') {
    return readSingleValue(value);
  }
  const selected = (value ?? []).filter((entry) => entry !== '');
  const shown = selected.slice(0, CHIP_VALUES_SHOWN).map((entry) => optionLabel(field, entry));
  const rest = selected.length - shown.length;
  const list = shown.join(', ');
  return rest > 0 ? t('filters.maisValores', { valores: list, n: rest }) : list;
}
